/* ============================================================================
   Ohr Labs — Newsletter signup (newsletter-signup.js)
   ----------------------------------------------------------------------------
   Teaser badge → email-capture modal (5% off). Ported behaviour from
   .design/design_handoff_newsletter, wired to the signup API.

   - Badge slides up ~5s after load AND after the visitor moves/scrolls, once per
     visitor (localStorage 'nl-dismissed'). Suppressed if already dismissed/subscribed.
   - Submit POSTs { email } to the endpoint (data-endpoint) and shows inline errors
     mapped from the API's error codes.
   - Excluded from the manual page (gated in layout) + checkout (its own layout).

   Structured for testing: pure helpers (isValidEmail / classifyResponse) and
   initSignup(els, opts) take injected deps (fetch, storage, win), so
   tests/newsletter-signup.test.mjs exercises every path without a browser.
   ============================================================================ */
(function () {
  // ---- Pure helpers ----------------------------------------------------------

  function isValidEmail(email) {
    if (!email || typeof email !== 'string') return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  }

  // Map an API response to an outcome. Success covers explicit success plus the
  // "already there / just sent" cases (429 duplicate, already_subscribed) — those
  // aren't user-fixable errors. errorKey selects the inline message otherwise.
  function classifyResponse(ok, data) {
    data = data || {};
    if (data.success) return { success: true, errorKey: null };
    var code = data.error;
    if (code === 'already_subscribed' || code === 'duplicate_request') return { success: true, errorKey: null };
    if (code === 'invalid_email') return { success: false, errorKey: 'invalid-email' };
    if (code === 'email_failed') return { success: false, errorKey: 'retry' };
    // invalid_body / app_not_installed / issue_failed / method_not_allowed / unknown
    return { success: false, errorKey: 'generic' };
  }

  var DISMISS_KEY = 'nl-dismissed';

  // ---- Controller (DOM + deps injected) --------------------------------------

  function initSignup(els, opts) {
    opts = opts || {};
    var win = opts.win || (typeof window !== 'undefined' ? window : {});
    var doFetch = opts.fetch || (win.fetch ? win.fetch.bind(win) : null);
    var storage = opts.storage || (typeof localStorage !== 'undefined' ? localStorage : null);
    var raf = (win && win.requestAnimationFrame) ? win.requestAnimationFrame.bind(win) : function (f) { f(); };
    var endpoint = (els.scrim.getAttribute && els.scrim.getAttribute('data-endpoint')) || (els.scrim.dataset && els.scrim.dataset.endpoint) || '';
    var submitting = false;
    var lastFocused = null;

    function store(k, v) { try { if (storage) storage.setItem(k, v); } catch (e) {} }
    function stored(k) { try { return storage ? storage.getItem(k) : null; } catch (e) { return null; } }
    function isDismissed() { return !!stored(DISMISS_KEY); }
    function markDismissed() { store(DISMISS_KEY, '1'); }

    function on(el, type, fn) { if (el && el.addEventListener) el.addEventListener(type, fn); }

    // -- error message text (localised via the scrim's data-err-* attributes) --
    function attr(name) { return (els.scrim.getAttribute && els.scrim.getAttribute(name)) || ''; }
    function errorText(key) {
      if (key === 'invalid-email') return attr('data-err-invalid-email') || 'Please enter a valid email.';
      if (key === 'retry') return attr('data-err-retry') || 'Please try again.';
      if (key === 'network') return attr('data-err-network') || 'Please try again.';
      return attr('data-err-generic') || 'Something went wrong.';
    }
    function showError(key) {
      if (!els.error) return;
      els.error.textContent = errorText(key);
      els.error.removeAttribute('hidden');
    }
    function clearError() { if (els.error) { els.error.textContent = ''; els.error.setAttribute('hidden', ''); } }

    function setStep(step) { els.modal.setAttribute('data-active', step); }

    function setSubmitting(on2) {
      submitting = on2;
      if (!els.submitBtn) return;
      if (on2) els.submitBtn.setAttribute('disabled', '');
      else els.submitBtn.removeAttribute('disabled');
      var label = els.submitBtn.querySelector && els.submitBtn.querySelector('.nl-btn-label');
      var sending = els.submitBtn.getAttribute && els.submitBtn.getAttribute('data-sending');
      if (label && sending) {
        if (on2) { label.setAttribute('data-orig', label.textContent); label.textContent = sending; }
        else if (label.getAttribute('data-orig') != null) { label.textContent = label.getAttribute('data-orig'); }
      }
    }

    // -- badge --
    function hideBadge() { if (els.badge) els.badge.classList.remove('show'); }
    function showBadge() {
      if (!els.badge || isDismissed()) return;
      els.badge.removeAttribute('hidden');
      raf(function () { els.badge.classList.add('show'); });
    }

    // -- modal --
    function openModal() {
      lastFocused = (win.document && win.document.activeElement) || null;
      els.scrim.removeAttribute('hidden');
      raf(function () {
        els.scrim.classList.add('open');
        els.scrim.setAttribute('aria-hidden', 'false');
        if (els.email && els.email.focus) { try { els.email.focus(); } catch (e) {} }
      });
    }
    function closeModal() {
      els.scrim.classList.remove('open');
      els.scrim.setAttribute('aria-hidden', 'true');
      setTimeout(function () {
        els.scrim.setAttribute('hidden', '');
        setStep('1');
        clearError();
        setSubmitting(false);
        if (els.email) els.email.value = els.email.value; // keep value; reset handled by page nav
      }, 300);
      if (lastFocused && lastFocused.focus) { try { lastFocused.focus(); } catch (e) {} }
    }

    function onSuccess() {
      setStep('done');
      markDismissed();          // don't re-nag a subscriber
      setSubmitting(false);
      setTimeout(closeModal, 3200);
    }

    function submit() {
      if (submitting) return;
      var email = els.email ? String(els.email.value || '').trim() : '';
      if (!isValidEmail(email)) { showError('invalid-email'); if (els.email && els.email.focus) els.email.focus(); return; }
      clearError();
      if (!doFetch || !endpoint) { showError('network'); return; }
      setSubmitting(true);
      doFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ email: email })
      })
        .then(function (res) {
          return res.json().catch(function () { return {}; }).then(function (data) { return { ok: res.ok, data: data }; });
        })
        .then(function (r) {
          var c = classifyResponse(r.ok, r.data);
          if (c.success) onSuccess();
          else { setSubmitting(false); showError(c.errorKey); }
        })
        .catch(function () { setSubmitting(false); showError('network'); });
    }

    // -- wiring --
    on(els.badge, 'click', function () { openModal(); hideBadge(); });
    on(els.badgeX, 'click', function (e) { if (e && e.stopPropagation) e.stopPropagation(); markDismissed(); hideBadge(); });
    on(els.badgeX, 'keydown', function (e) {
      if (e && (e.key === 'Enter' || e.key === ' ')) { if (e.preventDefault) e.preventDefault(); if (e.stopPropagation) e.stopPropagation(); markDismissed(); hideBadge(); }
    });
    on(els.close, 'click', closeModal);
    on(els.scrim, 'click', function (e) { if (e && e.target === els.scrim) closeModal(); });
    on(els.form, 'submit', function (e) { if (e && e.preventDefault) e.preventDefault(); submit(); });
    on(els.scrim, 'keydown', function (e) {
      if (!e) return;
      if (e.key === 'Escape' && els.scrim.classList.contains('open')) { closeModal(); return; }
      if (e.key === 'Tab') trapFocus(e);
    });

    // Simple focus trap: keep Tab within the modal while open.
    function trapFocus(e) {
      if (!els.modal || !els.modal.querySelectorAll) return;
      var f = els.modal.querySelectorAll('a[href],button:not([disabled]),input,[tabindex]:not([tabindex="-1"])');
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      var active = win.document && win.document.activeElement;
      if (e.shiftKey && active === first) { if (e.preventDefault) e.preventDefault(); if (last.focus) last.focus(); }
      else if (!e.shiftKey && active === last) { if (e.preventDefault) e.preventDefault(); if (first.focus) first.focus(); }
    }

    // -- badge trigger: fire when BOTH ~5s elapsed AND the visitor has moved/scrolled --
    function armBadge() {
      if (isDismissed()) return;
      var elapsed = false, moved = false, done = false;
      function tryShow() { if (done || !elapsed || !moved || isDismissed()) return; done = true; cleanup(); showBadge(); }
      function onMove() { moved = true; tryShow(); }
      function cleanup() {
        if (!win.removeEventListener) return;
        win.removeEventListener('scroll', onMove);
        win.removeEventListener('pointermove', onMove);
        win.removeEventListener('touchstart', onMove);
      }
      setTimeout(function () { elapsed = true; tryShow(); }, opts.delay || 5000);
      if (win.addEventListener) {
        win.addEventListener('scroll', onMove, { passive: true });
        win.addEventListener('pointermove', onMove, { passive: true });
        win.addEventListener('touchstart', onMove, { passive: true });
      }
    }

    return {
      armBadge: armBadge, showBadge: showBadge, openModal: openModal, closeModal: closeModal,
      submit: submit, setStep: setStep, isDismissed: isDismissed
    };
  }

  // ---- Resolve elements from a document --------------------------------------
  function resolveEls(doc) {
    var scrim = doc.getElementById('nl-scrim');
    if (!scrim) return null;
    var modal = doc.getElementById('nl-modal');
    return {
      badge: doc.getElementById('nl-badge'),
      badgeX: doc.getElementById('nl-badge-x'),
      scrim: scrim,
      modal: modal,
      close: doc.getElementById('nl-close'),
      form: doc.getElementById('nl-form'),
      email: doc.getElementById('nl-email'),
      submitBtn: modal && modal.querySelector ? modal.querySelector('[data-submit]') : null,
      error: doc.getElementById('nl-error')
    };
  }

  // ---- Exports for tests -----------------------------------------------------
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { isValidEmail: isValidEmail, classifyResponse: classifyResponse, initSignup: initSignup, resolveEls: resolveEls };
  }

  // ---- Browser bootstrap -----------------------------------------------------
  if (typeof document === 'undefined') return;
  function boot() {
    var els = resolveEls(document);
    if (!els) return;
    initSignup(els, {}).armBadge();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
